import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { subscriptionService, Subscription, SubscriptionPlan, PaymentMethod, DiscountValidationResponse } from '../../services/subscription.service';

// Subscription state interface
export interface SubscriptionState {
  // Current subscription
  currentSubscription: Subscription | null;
  subscriptionLoading: boolean;
  subscriptionError: string | null;
  
  // Available plans
  plans: SubscriptionPlan[];
  plansLoading: boolean;
  plansError: string | null;
  
  // Payment methods
  paymentMethods: PaymentMethod[];
  paymentMethodsLoading: boolean;
  paymentMethodsError: string | null;
  
  // Discount validation
  discountValidation: DiscountValidationResponse | null;
  discountLoading: boolean;
  discountError: string | null;
  
  // Status polling
  isPollingStatus: boolean;
  statusPollingAttempts: number;
  maxPollingAttempts: number;
}

const initialState: SubscriptionState = {
  currentSubscription: null,
  subscriptionLoading: false,
  subscriptionError: null,
  
  plans: [],
  plansLoading: false,
  plansError: null,
  
  paymentMethods: [],
  paymentMethodsLoading: false,
  paymentMethodsError: null,
  
  discountValidation: null,
  discountLoading: false,
  discountError: null,
  
  isPollingStatus: false,
  statusPollingAttempts: 0,
  maxPollingAttempts: 10,
};

// Async thunks
export const fetchCurrentSubscription = createAsyncThunk(
  'subscription/fetchCurrent',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getCurrentSubscription();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscription');
    }
  }
);

export const fetchSubscriptionPlans = createAsyncThunk(
  'subscription/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getPlans();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans');
    }
  }
);

export const fetchPaymentMethods = createAsyncThunk(
  'subscription/fetchPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getPaymentMethods();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment methods');
    }
  }
);

export const validateDiscountCode = createAsyncThunk(
  'subscription/validateDiscount',
  async ({ code, planId }: { code: string; planId?: string }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.validateDiscountCode(code, planId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to validate discount code');
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancel',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.cancelSubscription();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel subscription');
    }
  }
);

export const reactivateSubscription = createAsyncThunk(
  'subscription/reactivate',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.reactivateSubscription();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reactivate subscription');
    }
  }
);

// Status polling thunk
export const pollSubscriptionStatus = createAsyncThunk(
  'subscription/pollStatus',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { subscription: SubscriptionState };
    
    if (state.subscription.statusPollingAttempts >= state.subscription.maxPollingAttempts) {
      return rejectWithValue('Maximum polling attempts reached');
    }
    
    try {
      const response = await subscriptionService.getCurrentSubscription();
      
      // If subscription is active or failed, stop polling
      if (response.success && 'data' in response && response.data) {
        const subscription = response.data;
        if (subscription.status === 'active' || subscription.status === 'cancelled' || subscription.status === 'expired') {
          return { subscription, shouldContinuePolling: false };
        }
      }
      
      // Continue polling if still pending/processing
      const subscription = response.success && 'data' in response ? response.data : null;
      return { subscription, shouldContinuePolling: true };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to poll subscription status');
    }
  }
);

// Subscription slice
const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearSubscriptionError: (state) => {
      state.subscriptionError = null;
    },
    clearPlansError: (state) => {
      state.plansError = null;
    },
    clearPaymentMethodsError: (state) => {
      state.paymentMethodsError = null;
    },
    clearDiscountValidation: (state) => {
      state.discountValidation = null;
      state.discountError = null;
    },
    startStatusPolling: (state) => {
      state.isPollingStatus = true;
      state.statusPollingAttempts = 0;
    },
    stopStatusPolling: (state) => {
      state.isPollingStatus = false;
      state.statusPollingAttempts = 0;
    },
    incrementPollingAttempts: (state) => {
      state.statusPollingAttempts += 1;
    },
    updateSubscriptionStatus: (state, action: PayloadAction<Subscription>) => {
      state.currentSubscription = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch current subscription
      .addCase(fetchCurrentSubscription.pending, (state) => {
        state.subscriptionLoading = true;
        state.subscriptionError = null;
      })
      .addCase(fetchCurrentSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        if (action.payload.success && action.payload.data) {
          // Handle nested subscription data structure from API
          const responseData = action.payload.data as any;
          const subscriptionData = responseData.subscription || responseData;
          state.currentSubscription = subscriptionData;
        }
      })
      .addCase(fetchCurrentSubscription.rejected, (state, action) => {
        state.subscriptionLoading = false;
        state.subscriptionError = action.payload as string;
      })
      
      // Fetch subscription plans
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.plansLoading = true;
        state.plansError = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        if (action.payload.success && action.payload.data) {
          state.plans = action.payload.data;
        }
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.plansLoading = false;
        state.plansError = action.payload as string;
      })
      
      // Fetch payment methods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.paymentMethodsLoading = true;
        state.paymentMethodsError = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.paymentMethodsLoading = false;
        if (action.payload.success && action.payload.data) {
          state.paymentMethods = action.payload.data;
        }
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.paymentMethodsLoading = false;
        state.paymentMethodsError = action.payload as string;
      })
      
      // Validate discount code
      .addCase(validateDiscountCode.pending, (state) => {
        state.discountLoading = true;
        state.discountError = null;
      })
      .addCase(validateDiscountCode.fulfilled, (state, action) => {
        state.discountLoading = false;
        if (action.payload.success && action.payload.data) {
          state.discountValidation = action.payload.data;
        }
      })
      .addCase(validateDiscountCode.rejected, (state, action) => {
        state.discountLoading = false;
        state.discountError = action.payload as string;
      })
      
      // Cancel subscription
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        if (action.payload.success && state.currentSubscription) {
          state.currentSubscription.status = 'cancelled';
          state.currentSubscription.cancelled_at = new Date().toISOString();
          state.currentSubscription.auto_renew = false;
        }
      })
      
      // Reactivate subscription
      .addCase(reactivateSubscription.fulfilled, (state, action) => {
        if (action.payload.success && state.currentSubscription) {
          state.currentSubscription.status = 'active';
          state.currentSubscription.cancelled_at = undefined;
          state.currentSubscription.auto_renew = true;
        }
      })
      
      // Poll subscription status
      .addCase(pollSubscriptionStatus.pending, (state) => {
        state.isPollingStatus = true;
      })
      .addCase(pollSubscriptionStatus.fulfilled, (state, action) => {
        const { subscription, shouldContinuePolling } = action.payload;
        
        if (subscription) {
          state.currentSubscription = subscription;
        }
        
        if (!shouldContinuePolling) {
          state.isPollingStatus = false;
          state.statusPollingAttempts = 0;
        } else {
          state.statusPollingAttempts += 1;
        }
      })
      .addCase(pollSubscriptionStatus.rejected, (state, action) => {
        state.isPollingStatus = false;
        state.subscriptionError = action.payload as string;
      });
  },
});

export const {
  clearSubscriptionError,
  clearPlansError,
  clearPaymentMethodsError,
  clearDiscountValidation,
  startStatusPolling,
  stopStatusPolling,
  incrementPollingAttempts,
  updateSubscriptionStatus,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
